import { useEffect, useMemo, useState } from 'react';
import { EntityCard, EntityList, SectionCard, TabsRow, rowTitle } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { bills, products, wallet } from '@/api/endpoints';
import { pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Empty, ErrorBox, Loading } from '@/components/ui';
import { str } from '@/utils/format';
import { isOpenOrderStatus, orderTitle } from '@/utils/labels';

type Tab = 'units' | 'games' | 'bills';

const ORDER_TABS: Array<{ key: Tab; label: string }> = [
  { key: 'units', label: 'الوحدات' },
  { key: 'games', label: 'الألعاب' },
  { key: 'bills', label: 'الفواتير' },
];

export function OrdersScreen() {
  const [tab, setTab] = useState<Tab>('units');

  const state = useAsync(
    async () => {
      const [units, games, billOrders] = await Promise.all([
        wallet.orders(),
        products.orders(),
        bills.orders(),
      ]);
      return {
        success: true as const,
        units: units.success ? pickList(units, 'orders', 'items', 'data') : [],
        games: games.success ? pickList(games, 'orders', 'items', 'data') : [],
        bills: billOrders.success ? pickList(billOrders, 'orders', 'items', 'data') : [],
        unitsError: units.success ? '' : String(units.error ?? ''),
        gamesError: games.success ? '' : String(games.error ?? ''),
        billsError: billOrders.success ? '' : String(billOrders.error ?? ''),
      };
    },
    (p) => ({
      units: (p.units as JsonMap[]) ?? [],
      games: (p.games as JsonMap[]) ?? [],
      bills: (p.bills as JsonMap[]) ?? [],
      unitsError: str(p.unitsError, ''),
      gamesError: str(p.gamesError, ''),
      billsError: str(p.billsError, ''),
    }),
    [],
  );

  const data = state.data;
  const list = useMemo(() => {
    if (!data) return [] as JsonMap[];
    return tab === 'units' ? data.units : tab === 'games' ? data.games : data.bills;
  }, [data, tab]);

  const hasOpen = useMemo(
    () =>
      Boolean(
        data &&
          [...data.units, ...data.games, ...data.bills].some((o) =>
            isOpenOrderStatus(o.display_status ?? o.status),
          ),
      ),
    [data],
  );

  useEffect(() => {
    if (!hasOpen) return;
    const timer = window.setInterval(() => state.reload(), 2000);
    return () => window.clearInterval(timer);
  }, [hasOpen, state.reload]);

  if (state.loading && !data) return <Loading />;
  if (state.error && !data) return <ErrorBox message={state.error} onRetry={state.reload} />;
  if (!data) return <Empty title="لا توجد طلبات" />;

  const tabError =
    tab === 'units' ? data.unitsError : tab === 'games' ? data.gamesError : data.billsError;

  return (
    <div className="stack-gap customer-page">
      <SectionCard
        title="طلباتي"
        subtitle="وحدات · ألعاب · فواتير"
        icon={<Icon name="orders" size={24} />}
      >
        <TabsRow tabs={ORDER_TABS} value={tab} onChange={setTab} />

        {tabError ? <ErrorBox message={tabError} onRetry={state.reload} /> : null}

        {list.length === 0 ? (
          <Empty title="لا توجد طلبات" />
        ) : (
          <EntityList>
            {list.map((o, i) => {
              const status = str(o.display_status ?? o.status);
              const err = str(o.error_message, '');
              return (
                <EntityCard
                  key={str(o.id ?? o.order_id, String(i))}
                  className="list-card"
                  title={orderTitle(o)}
                  meta={rowTitle(o.target ?? o.phone ?? o.player_id, o.created_at)}
                  meta2={
                    err && ['rejected', 'failed', 'refunded'].includes(status) ? err : undefined
                  }
                  amount={o.cost ?? o.price ?? o.amount}
                  amountSuffix={tab === 'games' ? '$' : 'ل.س'}
                  status={status}
                />
              );
            })}
          </EntityList>
        )}
      </SectionCard>
    </div>
  );
}
